'use client'
import { useEffect, useState } from "react";
import Link from "next/link";

export type Toc = {
    value: string;
    depth: number;
    url: string;
}[];

interface TOCInlineProps {
    toc: Toc;
    indentDepth?: number;
    fromHeading?: number;
    toHeading?: number;
    asDisclosure?: boolean;
    exclude?: string | string[];
    defaultimage?: string;
}

export const useMDXHeadings = () => {
    useEffect(() => {
        if (typeof document !== "undefined") { 
            const headings = document.querySelectorAll("h1, h2, h3, h4, h5, h6");
            headings.forEach((heading) => {
                if (!heading.id) {
                    const text = heading.textContent?.trim() || "";
                    const id = text
                        .toLowerCase()
                        .replace(/\s+/g, "-")
                        .replace(/[^\w-]+/g, ""); 

                    heading.id = id;
                }
            });
            const components = document.querySelectorAll('[data-component="MainGrid"]');
            components.forEach((component) => {
                const title = component.getAttribute('data-title');
                if (title) {
                    const id = title
                        .toLowerCase()
                        .replace(/\s+/g, "-")
                        .replace(/[^\w-]+/g, "");

                    component.setAttribute('id', id); 
                }
            });
        }
    }, []);
};

export const ContentGrid = ({
    toc,
    indentDepth = 3,
    asDisclosure = false,
}: TOCInlineProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const toggleAnswer = () => {
        setIsOpen(!isOpen);
    };

    useMDXHeadings();

    const [isClient, setIsClient] = useState(false);

    useEffect(() => {
        setIsClient(true);
    }, []);

    const finalToc = isClient ? Array.from(
        document.querySelectorAll('h1, h2, h3, h4, h5, h6, [data-title]')
    ).map((element) => {
        if (element.tagName.match(/^H[1-3]$/)) {
            const heading = toc.find(
                (h) => h.value === element.textContent?.trim()
            );
            return heading
                ? {
                    value: heading.value,
                    url: heading.url,
                    depth: heading.depth
                }
                : null;
        } else if (element.hasAttribute('data-title')) {
            return {
                value: element.getAttribute('data-title') || '',
                url: `#${element.getAttribute('id')}`,
                depth: 2 
            };
        }
        return null;
    }).filter(Boolean) : []; 

    const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, url: string) => {
        e.preventDefault();
        const target = document.getElementById(url);
        if (target) {
            target.scrollIntoView({ behavior: "smooth", block: "start" });
            window.history.pushState(null, "", `${url}`);
        }
    };

    const tocList = (
        <div className="space-y-6 gap-x-5">
            <div className="fixed left-0 top-0 h-full w-64 overflow-y-auto bg-white shadow-md">
                <div>
                    <dl className="toggle" style={{ border: "none", background: "", color: "black", borderRadius: "10px", boxShadow: "0 0 10px 0 rgba(0,0,0,0.2)" }}>
                        <dt onClick={toggleAnswer}>
                            <a className="px-10" style={{ borderTop: 'none', paddingLeft: "10px", paddingRight: "10px", fontSize: "20px" }}>
                                <b>Table Contents</b>
                            </a>
                        </dt>
                        {isOpen && (
                            <dd className="black faqAnswer">
                                <ol className="list-none pl-9 mb-20" style={{ listStyle: "disc" }}>
                                    {isClient && finalToc.map((heading) => (
                                        <li
                                            key={`${heading.url}-${heading.depth}`}
                                            className={`hover:text-blue-600 ${heading.depth >= indentDepth ? "ml-6" : ""}`}
                                        >
                                            <Link
                                                href={heading.url}
                                                onClick={(e) => handleNavigation(e, heading.url)}
                                                className="text-gray-700 no-underline hover:text-rose-500"
                                                style={{ textDecoration: "none" }}
                                            >
                                                {heading.value}
                                            </Link>
                                        </li>
                                    ))}
                                </ol>
                            </dd>
                        )}
                    </dl>
                </div>
            </div>
        </div>
    );

    return (
        <div className="">
            <div className="blog-media mb-30 mb-xs-30 mt-30">
            {!asDisclosure && tocList}
            </div>
        </div>
    );
};

export default ContentGrid;
